/**
 * QUOTATION ITEM API — le cinque operazioni sulle righe del preventivo.
 *
 * ```
 * GET    /quotation/{id}/item                    -> Page<QuotationItem>
 * POST   /quotation/{id}/item                    -> QuotationItem
 * GET    /quotation/{id}/item/{itemId}           -> QuotationItem
 * PATCH  /quotation/{id}/item/{itemId}           -> QuotationItem
 * DELETE /quotation/{id}/item/{itemId}           -> 200
 * PUT    /quotation/{id}/item-order              -> Page<QuotationItem>
 * ```
 *
 * Risorsa annidata sotto il preventivo: il sito si deriva lato server dal
 * preventivo, quindi nessuna operazione porta un `groupId`. Non è una
 * semplificazione del client — è il backend a non accettarlo, perché una riga
 * fuori dal suo preventivo non ha senso.
 *
 * Non usa `createCrudThunks`: quel factory presuppone `create(groupId, payload)`
 * e `getById(id)`, mentre qui ogni operazione è scopata sul preventivo padre.
 * Stessa ragione per cui non lo usa `quotationFileApi`.
 */

import type { Page } from '@crm/shared';
import { httpClient } from '../../api/httpClient';
import {
  quotationItemFromDto,
  quotationItemToInput,
  type QuotationItem,
  type QuotationItemDto,
} from './quotationItemMappers';

const BASE = '/quotation';

type ItemPatch = Partial<Pick<QuotationItem, 'position' | 'quantity' | 'amount' | 'notes'>>;

export const quotationItemApi = {
  getByQuotation: (quotationId: number): Promise<Page<QuotationItem>> =>
    httpClient
      .get<Page<QuotationItemDto>>(`${BASE}/${quotationId}/item`)
      .then((r) => ({ ...r.data, items: r.data.items.map(quotationItemFromDto) })),

  getById: (quotationId: number, quotationItemId: number): Promise<QuotationItem> =>
    httpClient
      .get<QuotationItemDto>(`${BASE}/${quotationId}/item/${quotationItemId}`)
      .then((r) => quotationItemFromDto(r.data)),

  /** `position` omessa o zero: la riga si accoda in fondo. */
  create: (quotationId: number, payload: ItemPatch): Promise<QuotationItem> =>
    httpClient
      .post<QuotationItemDto>(`${BASE}/${quotationId}/item`, quotationItemToInput(payload))
      .then((r) => quotationItemFromDto(r.data)),

  update: (
    quotationId: number,
    quotationItemId: number,
    patch: ItemPatch,
  ): Promise<QuotationItem> =>
    httpClient
      .patch<QuotationItemDto>(
        `${BASE}/${quotationId}/item/${quotationItemId}`,
        quotationItemToInput(patch),
      )
      .then((r) => quotationItemFromDto(r.data)),

  remove: (quotationId: number, quotationItemId: number): Promise<void> =>
    httpClient.delete(`${BASE}/${quotationId}/item/${quotationItemId}`).then(() => undefined),

  /**
   * Riscrive le posizioni di TUTTE le righe nell'ordine dato, in una transazione
   * sola. Non è un `update` ripetuto: spostare una riga cambia la posizione di
   * tutte quelle che scavalca, e una raffica di PATCH interrotta a metà
   * lascerebbe un ordine che non è né il vecchio né il nuovo.
   *
   * La lista deve nominare tutte le righe una volta ciascuna: se il server
   * risponde 400 vuol dire che qualcuno ne ha aggiunta o tolta una nel
   * frattempo, e la risposta giusta è rileggere l'elenco.
   */
  reorder: (quotationId: number, quotationItemIds: number[]): Promise<QuotationItem[]> =>
    httpClient
      .put<Page<QuotationItemDto>>(`${BASE}/${quotationId}/item-order`, { quotationItemIds })
      .then((r) => r.data.items.map(quotationItemFromDto)),
};
